import React, { useState } from "react";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";

const SectionStyled = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 8px;
`;

const SectionDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 550px;
  margin-bottom: 48px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 48px;
  border-bottom: 1px solid var(--nd-border);
`;

const Tab = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => active ? "var(--nd-text-display)" : "transparent"};
  color: ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-text-disabled)"};
  cursor: pointer;
  outline: none;
  transition: all 200ms cubic-bezier(0.25, 0.1, 0.25, 1);

  &:hover { color: var(--nd-text-primary); }
`;

const ExampleCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  &:hover { border-color: var(--nd-border-visible); }
`;

const CardLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
  display: block;
`;

const CardTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 8px;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin-bottom: 16px;
  flex-grow: 1;
`;

const CardMeta = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const BottomCTA = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  text-align: center;
  margin-top: 48px;
  font-style: italic;
`;

const patterns = [
  { label: "OBSERVABILITY", title: "The 3-tier observability setup that works for 90% of startups", desc: "Most startups either monitor nothing or monitor everything. Both are wrong. Here's what we set up by default and why.", meta: "8 MIN READ" },
  { label: "ARCHITECTURE", title: "Why we stopped using service mesh for teams under 20 engineers", desc: "Istio is powerful. It's also a full-time job. For most teams, it's complexity you don't need yet.", meta: "6 MIN READ" },
  { label: "NETWORKING", title: "Our default VPC layout and why it looks like that", desc: "Public subnet, private subnet, NAT gateway, security groups. The boring foundation that prevents exciting outages.", meta: "5 MIN READ" },
  { label: "ARCHITECTURE", title: "When to use ECS instead of Kubernetes", desc: "More often than you think. If your team is under 10 and you're on AWS, K8s might be overkill. Here's how we decide.", meta: "7 MIN READ" },
  { label: "CI/CD", title: "The CI/CD pipeline structure we copy to every project", desc: "Lint, test, build, scan, deploy. Five stages, no surprises. We've refined this over 50+ projects.", meta: "5 MIN READ" },
];

const decisions = [
  { label: "ARCHITECTURE", title: "Multi-region vs single-region for a Series A", desc: "They wanted multi-region. We said not yet. That saved them $40K/year and six months of complexity they didn't need.", meta: "CONTEXT → DECISION → RESULT" },
  { label: "CI/CD", title: "Why we chose ArgoCD over FluxCD for this client", desc: "Both are good. One was better for this team's workflow. The decision came down to something nobody writes about in comparison posts.", meta: "CONTEXT → DECISION → RESULT" },
  { label: "ARCHITECTURE", title: "Splitting the monolith — when we said 'not yet'", desc: "The CTO wanted microservices. We looked at their team size, deploy frequency, and failure rate, and said wait six months.", meta: "CONTEXT → DECISION → RESULT" },
  { label: "INFRASTRUCTURE", title: "EKS vs GKE vs self-managed — how we decide", desc: "It's not about which is 'better.' It's about what your team can actually operate at 2 AM when something breaks.", meta: "CONTEXT → DECISION → RESULT" },
  { label: "PLATFORM", title: "When to invest in platform engineering", desc: "If you have fewer than 5 teams deploying independently, you probably don't need a platform team. Here's the threshold.", meta: "CONTEXT → DECISION → RESULT" },
];

const warStories = [
  { label: "INCIDENT", title: "The deploy that took down production for 4 hours", desc: "A config change that passed every test. Deployed fine to staging. Broke everything in prod. The fix was one line.", meta: "WHAT HAPPENED → WHAT WE LEARNED" },
  { label: "COST", title: "When auto-scaling worked too well and we got a $12K AWS bill", desc: "Auto-scaling did exactly what we told it to do. The problem was what we told it to do. Now we always set ceilings.", meta: "WHAT HAPPENED → WHAT WE LEARNED" },
  { label: "STATE", title: "The Terraform state file that three people edited at once", desc: "No locking, no remote state. Three engineers, one Monday morning, zero working infrastructure. We fixed it in an hour but it felt like a year.", meta: "WHAT HAPPENED → WHAT WE LEARNED" },
  { label: "DNS", title: "A DNS migration that looked fine for 6 hours, then wasn't", desc: "TTL was set to 6 hours. The old records were cached everywhere. We learned to always lower TTL 48 hours before a migration.", meta: "WHAT HAPPENED → WHAT WE LEARNED" },
  { label: "DATA", title: "The junior who accidentally deleted the production database", desc: "No blame. We had backups, we had runbooks, we recovered in 20 minutes. Then we added the guardrails that should have been there.", meta: "WHAT HAPPENED → WHAT WE LEARNED" },
];

const tabs = [
  { key: "patterns", label: "Patterns", data: patterns },
  { key: "decisions", label: "Decisions", data: decisions },
  { key: "war-stories", label: "War Stories", data: warStories },
];

const Examples = () => {
  const [activeTab, setActiveTab] = useState("patterns");
  const currentData = tabs.find(t => t.key === activeTab).data;

  return (
    <SectionStyled>
      <Container>
        <SectionLabel>FROM THE FIELD</SectionLabel>
        <SectionTitle>Stuff we've learned the hard way</SectionTitle>
        <SectionDesc>
          15 years of production infrastructure. Some patterns,
          some decisions, some things that went wrong.
        </SectionDesc>

        <TabRow>
          {tabs.map(t => (
            <Tab key={t.key} active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </Tab>
          ))}
        </TabRow>

        <Row>
          {currentData.map((item, i) => (
            <Col md="6" key={i}>
              <ExampleCard>
                <CardLabel>{item.label}</CardLabel>
                <CardTitle>{item.title}</CardTitle>
                <CardDesc>{item.desc}</CardDesc>
                <CardMeta>{item.meta}</CardMeta>
              </ExampleCard>
            </Col>
          ))}
        </Row>

        <BottomCTA>
          This is the kind of experience that doesn't come from tutorials.
          If you want someone on your team who's already made these mistakes, let's talk.
        </BottomCTA>
      </Container>
    </SectionStyled>
  );
};

export default Examples;
